/**
 * Dial Momentum — visual reinforcement for the dialing grind.
 *
 * Rewards the controllable action (a logged dial), not the rare deal. Reframes
 * every "no" as progress toward the statistically-expected next lead.
 *
 * Read-only: renders GET /api/dialer/momentum. Two surfaces, one component:
 *   - variant="band"  → persistent strip in WholesaleOverview (passive)
 *   - variant="hud"   → live panel in DialerPanel (pulses on each logged dial)
 *
 * The dialer bumps `refreshSignal` after every logged disposition → optimistic
 * +1 (instant ring/counter movement), a micro-pulse, then a refetch.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import './DialMomentum.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function DialMomentum({ variant = 'band', refreshSignal = 0 }) {
  const { authFetch } = useAuth();
  const [m, setM] = useState(null);
  const [bump, setBump] = useState(0);     // optimistic dials since last fetch
  const [pulse, setPulse] = useState(false);
  const firstRun = useRef(true);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/api/dialer/momentum`);
      const d = await r.json();
      if (d.success && d.data) { setM(d.data); setBump(0); }
    } catch { /* keep prior snapshot on transient error */ }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  // Each logged dial: optimistic +1, pulse, refetch (which clears the bump).
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setBump((b) => b + 1);
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 600);
    load();
    return () => clearTimeout(t);
  }, [refreshSignal, load]);

  if (!m) return null;

  const goal = m.today.goal || 75;
  const dials = m.today.dials + bump;
  const ringPct = Math.min(100, Math.round((dials / goal) * 100));
  const goalHit = dials >= goal;
  const remaining = Math.max(0, m.nextLead.target - (m.nextLead.since + bump));
  const leadPct = m.nextLead.target > 0
    ? Math.min(100, Math.round(((m.nextLead.since + bump) / m.nextLead.target) * 100))
    : 0;
  const streak = m.streak?.days || 0;
  const basis = m.ratios?.basis || 'seed';

  // Ring geometry (SVG stroke-dashoffset)
  const R = variant === 'hud' ? 34 : 26;
  const C = 2 * Math.PI * R;
  const size = (R + 6) * 2;

  return (
    <div className={`dm dm--${variant} ${pulse ? 'dm--pulse' : ''} ${goalHit ? 'dm--goalhit' : ''}`}>
      <div className="dm-ring-wrap">
        <svg className="dm-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle className="dm-ring-track" cx={size / 2} cy={size / 2} r={R} strokeWidth="5" fill="none" />
          <circle
            className="dm-ring-fill" cx={size / 2} cy={size / 2} r={R} strokeWidth="5" fill="none"
            strokeDasharray={C} strokeDashoffset={C * (1 - ringPct / 100)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <div className="dm-ring-center">
          <span className="dm-count">{dials}</span>
          <span className="dm-goal">/ {goal}</span>
        </div>
      </div>

      <div className="dm-body">
        <div className="dm-top">
          <span className="dm-title">Today's momentum</span>
          {streak > 0 && <span className="dm-streak">{streak}-day streak</span>}
        </div>

        <div className="dm-nextlead">
          <div className="dm-nextlead-label">
            {remaining === 0
              ? <strong>Lead overdue — keep dialing</strong>
              : <><strong>{remaining}</strong> dials to your next lead</>}
          </div>
          <div className="dm-bar"><div className="dm-bar-fill" style={{ width: `${leadPct}%` }} /></div>
        </div>

        {variant === 'hud' && (
          <div className="dm-deal">
            <div className="dm-deal-label">
              deal progress <span className="dm-basis">{basis}</span>
            </div>
            <div className="dm-bar dm-bar--deal"><div className="dm-bar-fill" style={{ width: `${m.deal.pct}%` }} /></div>
          </div>
        )}
      </div>
    </div>
  );
}
